import React, { useState, useEffect } from "react";
import SEO from "../seo";
const Product = () => {
  const [activeSection, setActiveSection] = useState("overview");

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        "overview",
        "get-started",
        "key-features",
        "example-workflow",
        "product-options",
        "add-options",
        "generate-variants",
        "steps-variants",
        "benefits-variants",
      ];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <SEO
        title="Product Creation | xPage Help Center"
        description="Learn how to create high-converting product pages with xPage. Use AI-generated pages, manual entry, variants, and Shopify integration for faster setup and better sales performance."
        keywords="xPage, product creation, AI landing page, product variants, Shopify integration, product options, generate product page, eCommerce tools"
        canonical="https://help.xpage.ai/help/store-operation/Physical%20Product"
        type="article"
      />
      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Header Section */}
        <header className="mb-6">
          <h1 className="text-systemTitle text-2xl font-semibold mb-3">
            Physical Product
          </h1>
          <p className="text-sm text-systemLabel">
            xPage offers a streamlined, AI-powered landing page creation system
            that lets you build high-converting landing pages in minutes. Thanks
            to its intuitive interface and smart automation, it's effortless to
            generate and manage product listings, even without design or coding
            experience.
          </p>
        </header>

        {/* Get Started Section */}
        <section id="get-started" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Get started with product creation
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-4">
              When you first access the Products page in xPage, you'll find two
              primary options to begin crafting your product pages:
            </p>

            <table className="w-full mb-6">
              <tbody className="border divide-y divide-systemBorder">
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">Option</strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Description
                  </td>
                </tr>
                <tr className="">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Generate
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Click Generate, then simply paste a product URL (like an
                    AliExpress or competitor product link) ,you can also add a
                    product via Images and Prompt or manually xPage will
                    instantly create a tailored product page optimized for
                    conversion.
                  </td>
                </tr>
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Create manually
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Prefer full control? Input your product details manually:
                    add descriptions, pricing, variants, and images just like
                    traditional product setup.
                  </td>
                </tr>
              </tbody>
            </table>

            <p className="text-sm text-systemLabel">
              Once you've chosen your path, the system will guide you through
              key steps such as setting up product titles, specifications,
              imagery, variant options, and pricing for seamless publishing.
            </p>
          </div>
        </section>

        {/* Key Features Section */}
        <section id="key-features" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Key features and benefits
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <table className="w-full mb-6">
              <tbody className="border divide-y divide-systemBorder">
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Feature
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Description
                  </td>
                </tr>
                <tr className="">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      AI-generated pages
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Transform a single product link into a complete,
                    ready-to-use product page automated for speed and
                    performance.
                  </td>
                </tr>

                <tr className="">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Shopify integration
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Once your landing page is generated, you can import it
                    directly into your Shopify store with a single click.
                  </td>
                </tr>
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Optimized for conversions
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Created pages are structured for fast loading, A/B testing,
                    and mobile first responsiveness powering better ad
                    performance and user experience.
                  </td>
                </tr>
                <tr className="">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Manual creation support
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    If you prefer custom listings, xPage supports traditional
                    product entry allowing fine-tuned control over specs,
                    variants, and visuals.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Example Workflow Section */}
        <section id="example-workflow" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Example workflow
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <ol className="list-decimal list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li className="mb-2">
                <span className="font-medium">
                  In the xPage dashboard, click Generate to let AI craft a
                  product page instantly, or choose a manual option.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Paste the product link (e.g., from AliExpress) if using AI, or
                  begin entering your product details.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Select a template and customize as needed.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Preview your product page, adjust settings (like pricing or
                  SEO), then Publish or Import to Shopify directly.
                </span>
              </li>
            </ol>
          </div>
        </section>

        {/* Product Options Section */}
        <section id="product-options" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Product options & variants
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-4">
              xPage allows you to add multiple options to a product such as
              size, color, or material and automatically create variants for
              each combination. This ensures you can manage inventory precisely
              and offer customers the exact configurations they want.
            </p>
          </div>
        </section>

        {/* Add Product Options Section */}
        <section id="add-options" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Add product options
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-4">
              When creating or editing a product in the xPage admin, you can
              define one or more options (e.g., Size, Color, Style). Each option
              can have multiple values.
            </p>

            <div className="bg-system200 rounded-lg p-3 mb-4">
              <p className="text-sm text-systemLabel font-semibold mb-2">
                Example:
              </p>
              <p className="text-sm text-systemLabel">
                Option: Size → Values: Small, Medium, Large
              </p>
              <p className="text-sm text-systemLabel">
                Option: Color → Values: Red, Blue, Black
              </p>
            </div>
          </div>
        </section>

        {/* Generate Variants Section */}
        <section id="generate-variants" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Generate variants
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-4">
              Once you add product options, xPage automatically generates all
              possible variants based on the combinations. Each variant can
              have:
            </p>

            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li>Its own SKU</li>
              <li>Price (same as base price or customized per variant)</li>
              <li>Stock quantity</li>
              <li>
                Image (to match the variant, e.g., show a red product when "Red"
                is selected)
              </li>
            </ul>
          </div>
        </section>

        {/* Steps to Add Options & Variants Section */}
        <section id="steps-variants" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Steps to add options & variants
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <ol className="list-decimal list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li className="mb-2">
                <span className="font-medium">
                  In the xPage admin, go to Products.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">Create or edit a product.</span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Scroll to the Options & Variants section.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Click Add Option and enter the name (e.g., Size).
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Add the option values (e.g., Small, Medium, Large).
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Repeat for additional options (e.g., Color).
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Review the automatically generated variant list.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Adjust pricing, SKUs, stock levels, and images as needed.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">Click Save.</span>
              </li>
            </ol>
          </div>
        </section>

        {/* Benefits of Using Variants Section */}
        <section id="benefits-variants" className="scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Benefits of using variants
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <table className="w-full mb-6">
              <tbody className="border divide-y divide-systemBorder">
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Benefit
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Description
                  </td>
                </tr>
                <tr className="">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Better customer experience
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Customers can choose exactly what they want from a single
                    product page.
                  </td>
                </tr>
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Inventory accuracy
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Track stock per variant to avoid overselling.
                  </td>
                </tr>
                <tr className="">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Marketing flexibility
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Highlight popular variants in ads or promotions.
                  </td>
                </tr>
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Faster setup
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Automatic generation of all variant combinations saves time.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </div>

      {/* Table of Contents Sidebar */}
      <div className="lg:w-64 flex-shrink-0 lg:block hidden">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            <li>
              <a
                href="#overview"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "overview"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Overview
              </a>
            </li>
            <li>
              <a
                href="#get-started"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "get-started"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Get Started
              </a>
            </li>
            <li>
              <a
                href="#key-features"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "key-features"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Key Features
              </a>
            </li>
            <li>
              <a
                href="#example-workflow"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "example-workflow"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Example Workflow
              </a>
            </li>
            <li>
              <a
                href="#product-options"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "product-options"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Product Options
              </a>
            </li>
            <li>
              <a
                href="#add-options"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "add-options"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Add Options
              </a>
            </li>
            <li>
              <a
                href="#generate-variants"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "generate-variants"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Generate Variants
              </a>
            </li>
            <li>
              <a
                href="#steps-variants"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "steps-variants"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Steps for Variants
              </a>
            </li>
            <li>
              <a
                href="#benefits-variants"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "benefits-variants"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Benefits of Variants
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Product;
